import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const SUPABASE_URL = 'https://nvmyabvxofnfvrnbehkm.supabase.co';
const ALIAS_HOSTS = new Set(['data.cannafax.ai']);

/**
 * Normalize any accepted image reference to a bare `listing_images` storage
 * path. Returns null when the input references something other than the
 * `listing_images` bucket (external URL, already-signed URL, etc.), in which
 * case the caller should pass the value through unchanged.
 *
 * Accepts:
 *   - Bare path         `{listing_id}/{file.ext}`
 *   - Public URL        `https://<proj>.supabase.co/storage/v1/object/public/listing_images/{path}`
 *   - Alias public URL  `https://data.cannafax.ai/storage/v1/object/public/listing_images/{path}`
 */
const extractListingImagePath = (raw: string): string | null => {
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return null;
    }
    const canonicalHost = SUPABASE_URL.replace(/^https?:\/\//, '');
    const isSupabaseHost = url.hostname === canonicalHost || ALIAS_HOSTS.has(url.hostname);
    if (!isSupabaseHost) return null;
    const m = url.pathname.match(/\/storage\/v1\/object\/public\/listing_images\/(.+)$/);
    if (!m) return null;
    try {
      return m[1].split('/').map((s) => decodeURIComponent(s)).join('/');
    } catch {
      return m[1];
    }
  }
  return raw.replace(/^\/+/, '');
};

// Global in-memory cache for resolved URLs (50-min TTL, within 1-hour signed URL validity)
const URL_CACHE_TTL = 50 * 60 * 1000;
const urlCache = new Map<string, { url: string; expires: number }>();

/** Check cache synchronously */
export const getCachedUrl = (imageUrl: string): string | null => {
  const cached = urlCache.get(imageUrl);
  if (cached && Date.now() < cached.expires) return cached.url;
  if (cached) urlCache.delete(imageUrl);
  return null;
};

const cacheUrl = (imageUrl: string, resolvedUrl: string) => {
  urlCache.set(imageUrl, { url: resolvedUrl, expires: Date.now() + URL_CACHE_TTL });
};

/**
 * Hook to resolve image URLs to signed URLs for private storage buckets
 */
export const useImageUrls = (images: string[] | null | undefined) => {
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>(() => {
    if (!images || images.length === 0) return {};
    const initial: Record<string, string> = {};
    for (const img of images) {
      const cached = getCachedUrl(img);
      if (cached) initial[img] = cached;
    }
    return initial;
  });
  const [loading, setLoading] = useState(() => {
    if (!images || images.length === 0) return false;
    return images.some(img => !getCachedUrl(img));
  });

  const imagesKey = images ? JSON.stringify(images) : '';

  useEffect(() => {
    const resolveImageUrls = async () => {
      if (!images || images.length === 0) {
        setResolvedUrls({});
        setLoading(false);
        return;
      }

      // Check which URLs still need resolving
      const newResolvedUrls: Record<string, string> = {};
      const uncached: string[] = [];

      for (const imageUrl of images) {
        const cached = getCachedUrl(imageUrl);
        if (cached) {
          newResolvedUrls[imageUrl] = cached;
        } else {
          uncached.push(imageUrl);
        }
      }

      if (uncached.length === 0) {
        setResolvedUrls(newResolvedUrls);
        setLoading(false);
        return;
      }

      setLoading(true);

      for (const imageUrl of uncached) {
        try {
          // Normalize legacy public-URL rows down to bare paths so we sign
          // them (bucket is/becoming private). Non-listing_images external
          // URLs pass through unchanged.
          const listingPath = extractListingImagePath(imageUrl);
          if (!listingPath) {
            newResolvedUrls[imageUrl] = imageUrl;
            cacheUrl(imageUrl, imageUrl);
            continue;
          }

          const { data: listingData } = await supabase.storage
            .from('listing_images')
            .createSignedUrl(listingPath, 3600);

          if (listingData?.signedUrl) {
            newResolvedUrls[imageUrl] = listingData.signedUrl;
            cacheUrl(imageUrl, listingData.signedUrl);
            continue;
          }

          console.error('Failed to sign listing_images path:', listingPath);
          newResolvedUrls[imageUrl] = imageUrl;
        } catch (error) {
          console.error('Error resolving image URL:', imageUrl, error);
          newResolvedUrls[imageUrl] = imageUrl;
        }
      }

      setResolvedUrls(newResolvedUrls);
      setLoading(false);
    };

    resolveImageUrls();
  }, [imagesKey]);

  const getUrl = (imageUrl: string): string => {
    return resolvedUrls[imageUrl] || imageUrl;
  };

  return { resolvedUrls, getUrl, loading };
};

/**
 * Resolve a single image URL to a signed URL (with caching)
 */
export const resolveImageUrl = async (imageUrl: string): Promise<string> => {
  if (!imageUrl) return '';

  const cached = getCachedUrl(imageUrl);
  if (cached) return cached;

  try {
    const listingPath = extractListingImagePath(imageUrl);
    if (!listingPath) {
      cacheUrl(imageUrl, imageUrl);
      return imageUrl;
    }

    const { data: listingData } = await supabase.storage
      .from('listing_images')
      .createSignedUrl(listingPath, 3600);

    if (listingData?.signedUrl) {
      cacheUrl(imageUrl, listingData.signedUrl);
      return listingData.signedUrl;
    }

    console.error('Failed to sign listing_images path:', listingPath);
    return imageUrl;
  } catch (error) {
    console.error('Error resolving image URL:', error);
    return imageUrl;
  }
};
